//store/chatStore.ts

//store/chatStore.ts

"use client";

import { create } from "zustand";
import { persist } from "zustand/middleware";
import type {
  ChatSession,
  Message,
  ThemeKey,
  RightDrawerTab,
  Artifact,
  PreviewPanelState,
  MobileTab,
  ChatArchiveArtifact,
} from "@/types/chat";

// ─── UI Store (панели, тема, предпросмотр артефактов) ──────────────────────────

export interface UIState {
  leftOpen: boolean;
  rightDrawerTab: RightDrawerTab;
  theme: ThemeKey;
  preview: PreviewPanelState;
  mobileTab: MobileTab;

  toggleLeft: () => void;
  setLeftOpen: (v: boolean) => void;
  openDrawer: (tab: RightDrawerTab) => void;
  closeDrawer: () => void;
  setTheme: (t: ThemeKey) => void;

  // Управление третьей панелью (Preview)
  openPreview: (artifact: Artifact) => void;
  closePreview: () => void;
  setMobileTab: (tab: MobileTab) => void;
  searchOpen: boolean;
  setSearchOpen: (open: boolean) => void;

  archiveOpen: boolean;
  setArchiveOpen: (open: boolean) => void;

}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      leftOpen: true,
      rightDrawerTab: null,
      theme: "light",
      preview: { open: false, artifact: null },
      mobileTab: "chat",
      searchOpen: false,
      archiveOpen: false,
      setArchiveOpen: (open) => set({ archiveOpen: open }),
      setSearchOpen: (open) => set({ searchOpen: open }),

      toggleLeft: () => set((s) => ({ leftOpen: !s.leftOpen })),
      setLeftOpen: (v) => set({ leftOpen: v }),

      openDrawer: (tab) =>
        set((s) => ({
          rightDrawerTab: s.rightDrawerTab === tab ? null : tab,
        })),
      closeDrawer: () => set({ rightDrawerTab: null }),

      setTheme: (theme) => set({ theme }),

      openPreview: (artifact) =>
        set({ preview: { open: true, artifact }, mobileTab: "preview" }),
      closePreview: () =>
  set(s => ({ preview: { open: false, artifact: s.preview.artifact }, mobileTab: "chat" })),
      setMobileTab: (tab) => set({ mobileTab: tab }),
    }),
    
    { name: "chat-ui" }
  )
);

// ─── Chat Store (сессии, сообщения, артефакты) ────────────────────────────────

interface ChatState {
  sessions: ChatSession[];
  activeChatId: string | null;
  messages: Record<string, Message[]>;
  isThinking: boolean;
  currentTopic: string | null;
  activeChatSessionId: string | null;
  domains: any[];
  selectedDomain: any | null;
  teams: any[];
  selectedTeam: any | null; 
  //sendMessage: ((content: string) => void) | null;
  sendMessage: ((content: string, fileIds?: string[], fileNames?: Record<string, string>, opts?: { hidden?: boolean; economyMode?: boolean }) => void) | null;
  //setSendMessage: (fn: (content: string) => void) => void;
  //setSendMessage: (fn: (content: string, fileIds?: string[], fileNames?: Record<string, string>, hidden?: boolean) => void) => void;
  setSendMessage: (fn: (content: string, fileIds?: string[], fileNames?: Record<string, string>, opts?: { hidden?: boolean; economyMode?: boolean }) => void) => void;
  setActiveChat: (id: string | null) => void;
  addSession: (session: ChatSession) => void;
  toggleFavorite: (id: string) => void;
  addMessage: (chatId: string, msg: Message) => void;
  setIsThinking: (v: boolean) => void;
  appendToLastMessage: (chatId: string, content: string) => void;
  setActiveChatSessionId: (id: string) => void;
  setCurrentTopic: (topic: string) => void;
  setDomains: (domains: any[]) => void;
  setSelectedDomain: (domain: any) => void;
  setTeams: (teams: any[]) => void;
  setSelectedTeam: (team: any) => void;
  deleteSession: (id: string) => void;
  renameSession: (id: string, title: string) => void;
  duplicateSession: (id: string) => void;
  setSessions: (sessions: ChatSession[]) => void;
  // --- НОВОЕ ---
  addArtifactToSession: (sessionId: string, artifact: import("@/types/chat").ChatArchiveArtifact) => void;
  removeArtifactFromSession: (sessionId: string, artifactId: string) => void;
  activeMsgIndex: number; // Индекс сообщения, на котором сейчас пользователь
  scrollTargetIndex: number | null; // Индекс, к которому нужно проскроллить
  
  setActiveMsgIndex: (idx: number) => void;
  scrollToMessage: (idx: number) => void;
  


}

// ── Демо-артефакты для примера ──
const DEMO_CODE_ARTIFACT: Artifact = {
  id: "a1",
  type: "code",
  title: "ChatArea.tsx",
  lang: "typescript",
  filename: "ChatArea.tsx",
  content: `"use client";

import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useChatStore } from "@/store/chatStore";

export function ChatArea() {
  const { activeChatId, messages } = useChatStore();
  const bottomRef = useRef<HTMLDivElement>(null);
  const msgs = activeChatId ? (messages[activeChatId] ?? []) : [];

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs.length]);

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-3xl mx-auto px-6 py-4">
        <AnimatePresence initial={false}>
          {msgs.map((msg) => (
            <motion.div key={msg.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}>
              {msg.content}
            </motion.div>
          ))}
        </AnimatePresence>
        <div ref={bottomRef} />
      </div>
    </div>
  );
}`,
  createdAt: new Date(),
};

const DEMO_MD_ARTIFACT: Artifact = {
  id: "a2",
  type: "markdown",
  title: "README.md",
  filename: "README.md",
  content: `# Salaria AI

Next.js 14 · Tailwind · shadcn/ui · Framer Motion · Zustand

## Быстрый старт

\`\`\`bash
npm install
npm run dev
\`\`\`

## Темы

| Ключ | Описание |
|------|----------|
| light | Светлая |
| dark | Тёмная |
| sepia | Сепия |
`,
  createdAt: new Date(),
};

export const useChatStore = create<ChatState>()(
  persist(
    (set) => ({
      sessions: [],
      activeChatId: null,
      isThinking: false,
      currentTopic: null,
      activeChatSessionId: null,
      domains: [],
      selectedDomain: null,
      teams: [],
      selectedTeam: null,
      messages: {},
      activeMsgIndex: 0,
      scrollTargetIndex: null,

      setActiveMsgIndex: (idx) => set({ activeMsgIndex: idx }),
      scrollToMessage: (idx) => set({ scrollTargetIndex: idx }),
      
      setActiveChat: (id) => set({ activeChatId: id }),
      addSession: (session) =>
        set((s) => ({ sessions: [session, ...s.sessions] })),
      toggleFavorite: (id) =>
        set((s) => ({
          sessions: s.sessions.map((sess) =>
            sess.id === id ? { ...sess, isFavorite: !sess.isFavorite } : sess
          ),
        })),
        // Удаление сессии и связанных с ней сообщений
      deleteSession: (id) =>
        set((state) => {
          const newSessions = state.sessions.filter((s) => s.id !== id);
          
          // Если удаляем текущий активный чат — сбрасываем ID активного чата
          const newActiveId = state.activeChatId === id ? null : state.activeChatId;
          
          // Чистим сообщения этого чата из объекта messages
          const newMessages = { ...state.messages };
          delete newMessages[id];

          return {
            sessions: newSessions,
            activeChatId: newActiveId,
            messages: newMessages,
          };
        }),
        duplicateSession: (id) =>
            set((state) => {
              const session = state.sessions.find((s) => s.id === id);
              if (!session) return state;

              const newId = Date.now().toString(); // Генерируем новый ID
              const newSession = {
                ...session,
                id: newId,
                title: `${session.title} (Копия)`, // Помечаем, что это копия
                createdAt: new Date(),
                updatedAt: new Date(),
                isFavorite: false, // Копия по умолчанию не в избранном
              };

              // Копируем сообщения
              const sourceMessages = state.messages[id] || [];
              const newMessages = {
                ...state.messages,
                [newId]: [...sourceMessages],
              };

              return {
                sessions: [newSession, ...state.sessions],
                messages: newMessages,
                activeChatId: newId, // Переключаемся на новый клон
              };
            }),
      // Переименование сессии
      renameSession: (id, title) =>
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === id ? { ...s, title, updatedAt: new Date() } : s
          ),
        })),
        setSessions: (sessions) => set({ sessions }),

      // Проверка на думание чтобы раньше времени небыло иконки вообще
      setIsThinking: (v) => set({ isThinking: v }),

          addMessage: (chatId, msg) =>
            set((s) => ({
              messages: {
                ...s.messages,
                // Принудительно очищаем контент от лишних пробелов при создании
                [chatId]: [
                  ...(s.messages[chatId] ?? []), 
                  { ...msg, content: msg.content.trim() }
                ],
              },
            })),
            appendToLastMessage: (chatId, newContent) =>
              set((s) => {
                const chatMsgs = s.messages[chatId] ?? [];
                if (chatMsgs.length === 0) return s;

                const lastIdx = chatMsgs.length - 1;
                const lastMsg = chatMsgs[lastIdx];

                // ПРОВЕРКА: Если сообщение было пустым и пришел пробел — игнорируем.
                // Это не даст иконкам MsgActions "подумать", что текст уже появился.
                const isFirstEmptyChar = lastMsg.content === "" && newContent.trim() === "";
                const updatedContent = isFirstEmptyChar ? "" : lastMsg.content + newContent;

                const newMsgs = [...chatMsgs];
                newMsgs[lastIdx] = { ...lastMsg, content: updatedContent };

                return {
                  messages: { ...s.messages, [chatId]: newMsgs },
                };
              }),
                //Архив и артифакты работа.
                addArtifactToSession: (sessionId, artifact) =>
  set((state) => ({
    sessions: state.sessions.map((s) =>
      s.id === sessionId
        ? { ...s, artifacts: [...(s.artifacts ?? []), artifact] }
        : s
    ),
  })),

removeArtifactFromSession: (sessionId, artifactId) =>
  set((state) => ({
    sessions: state.sessions.map((s) =>
      s.id === sessionId
        ? { ...s, artifacts: (s.artifacts ?? []).filter((a) => a.id !== artifactId) }
        : s
    ),
  })),

      //appendToLastMessage: (chatId, content) =>
      //  set((s) => {
      //    const msgs = [...(s.messages[chatId] ?? [])];
      //    if (!msgs.length) return s;
     //     msgs[msgs.length - 1] = {
    //        ...msgs[msgs.length - 1],
    //        content: msgs[msgs.length - 1].content + content,
    //      };
   //       return { messages: { ...s.messages, [chatId]: msgs } };
   //     }),

      sendMessage: null,
      setSendMessage: (fn) => set({ sendMessage: fn }),
      setActiveChatSessionId: (id) => set({ activeChatSessionId: id }),
      setCurrentTopic: (topic) => set({ currentTopic: topic }),
      setDomains: (domains) => set({ domains }),
      setSelectedDomain: (domain) => set({ selectedDomain: domain }),
      setTeams: (teams) => set({ teams }),
      setSelectedTeam: (team) => set({ selectedTeam: team }),
    }),
    { 
      name: "chat-sessions",
      version: 3,
      partialize: (state) => ({
        selectedDomain: state.selectedDomain,
      }) as any,
    }
  )
);